import React, { useState, useEffect } from 'react';
import { 
  Navigation, 
  Shield, 
  Zap, 
  Target, 
  Users, 
  Award, 
  TrendingUp, 
  MapPin, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Globe,
  BarChart3,
  Smartphone,
  Car,
  Route,
  Gauge,
  Activity,
  Radio,
  Satellite
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import MainNavbar from '../../components/layout/MainNavbar';

const About = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState('light');
  const [vehicles, setVehicles] = useState([
    { id: 1, position: 0, speed: 0.3, color: 'red', name: 'Car 1', direction: 'left', type: 'car' },
  ]);

  // Car icon generator with different colors
  const getCarIcon = (color) => {
    const colors = {
      red: { body: '#dc2626', bodyDark: '#991b1b', bodyLight: '#ef4444', wave: '#dc2626' },
      blue: { body: '#2563eb', bodyDark: '#1e40af', bodyLight: '#3b82f6', wave: '#2563eb' },
      green: { body: '#16a34a', bodyDark: '#15803d', bodyLight: '#22c55e', wave: '#16a34a' },
      purple: { body: '#9333ea', bodyDark: '#7e22ce', bodyLight: '#a855f7', wave: '#9333ea' },
      orange: { body: '#ea580c', bodyDark: '#c2410c', bodyLight: '#f97316', wave: '#ea580c' },
    };
    
    const carColor = colors[color] || colors.red;
    
    const CarIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="227" viewBox="0 0 300 600">
      <!-- Shadow base -->
      <ellipse cx="150" cy="300" rx="95" ry="240" fill="#000" opacity="0.15"/>
      
      <!-- Car body -->
      <path d="M90 60 Q150 20 210 60 L235 150 Q245 300 235 450 L210 540 Q150 580 90 540 L65 450 Q55 300 65 150 Z" fill="${carColor.body}" stroke="${carColor.bodyDark}" stroke-width="2"/>
      
      <!-- Car body highlight -->
      <path d="M90 60 Q150 20 210 60 L235 150 Q245 300 235 450 L210 540 Q150 580 90 540 L65 450 Q55 300 65 150 Z" fill="${carColor.bodyLight}" opacity="0.3"/>
      
      <!-- Windshield -->
      <rect x="105" y="110" width="90" height="90" rx="18" fill="#bae6fd" opacity="0.7"/>
      
      <!-- Rear glass -->
      <rect x="105" y="380" width="90" height="90" rx="18" fill="#bae6fd" opacity="0.7"/>
      
      <!-- Hood detail -->
      <rect x="140" y="80" width="20" height="120" rx="10" fill="#ffffff" opacity="0.25"/>
      
      <!-- Wheels -->
      <rect x="45" y="170" width="30" height="80" rx="14" fill="#111827"/>
      <rect x="45" y="350" width="30" height="80" rx="14" fill="#111827"/>
      <rect x="225" y="170" width="30" height="80" rx="14" fill="#111827"/>
      <rect x="225" y="350" width="30" height="80" rx="14" fill="#111827"/>
      
      <!-- Wheel rims -->
      <rect x="52" y="188" width="16" height="44" rx="8" fill="#9ca3af"/>
      <rect x="52" y="368" width="16" height="44" rx="8" fill="#9ca3af"/>
      <rect x="232" y="188" width="16" height="44" rx="8" fill="#9ca3af"/>
      <rect x="232" y="368" width="16" height="44" rx="8" fill="#9ca3af"/>
      
      <!-- Headlights -->
      <rect x="105" y="60" width="40" height="12" rx="6" fill="#fde047"/>
      <rect x="155" y="60" width="40" height="12" rx="6" fill="#fde047"/>
      
      <!-- Tail lights -->
      <rect x="105" y="528" width="40" height="12" rx="6" fill="#7f1d1d"/>
      <rect x="155" y="528" width="40" height="12" rx="6" fill="#7f1d1d"/>
      
      <!-- Center direction line -->
      <line x1="150" y1="50" x2="150" y2="550" stroke="#ffffff" stroke-width="3" stroke-dasharray="10,10" opacity="0.5"/>
    </svg>`;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(CarIcon)}`;
  };

  // Truck icon generator
  const getTruckIcon = (color) => {
    const colors = {
      red: { body: '#dc2626', bodyDark: '#991b1b', bodyLight: '#ef4444', wave: '#dc2626' },
      blue: { body: '#2563eb', bodyDark: '#1e40af', bodyLight: '#3b82f6', wave: '#2563eb' },
      green: { body: '#16a34a', bodyDark: '#15803d', bodyLight: '#22c55e', wave: '#16a34a' },
      purple: { body: '#9333ea', bodyDark: '#7e22ce', bodyLight: '#a855f7', wave: '#9333ea' },
      orange: { body: '#ea580c', bodyDark: '#c2410c', bodyLight: '#f97316', wave: '#ea580c' },
    };
    
    const truckColor = colors[color] || colors.green;
    
    const TruckIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="80" viewBox="0 0 300 600">
      <!-- Shadow base -->
      <ellipse cx="150" cy="300" rx="110" ry="280" fill="#000" opacity="0.15"/>
      
      <!-- Truck cab -->
      <path d="M80 80 Q120 40 160 80 L180 140 Q190 220 180 300 L160 360 Q120 400 80 360 L60 300 Q50 220 60 140 Z" fill="${truckColor.body}" stroke="${truckColor.bodyDark}" stroke-width="2"/>
      
      <!-- Truck cab highlight -->
      <path d="M80 80 Q120 40 160 80 L180 140 Q190 220 180 300 L160 360 Q120 400 80 360 L60 300 Q50 220 60 140 Z" fill="${truckColor.bodyLight}" opacity="0.3"/>
      
      <!-- Truck cargo area -->
      <rect x="180" y="140" width="100" height="220" rx="8" fill="${truckColor.body}" stroke="${truckColor.bodyDark}" stroke-width="2"/>
      <rect x="180" y="140" width="100" height="220" rx="8" fill="${truckColor.bodyLight}" opacity="0.2"/>
      
      <!-- Cargo container lines -->
      <line x1="200" y1="160" x2="200" y2="360" stroke="${truckColor.bodyDark}" stroke-width="1" opacity="0.5"/>
      <line x1="220" y1="160" x2="220" y2="360" stroke="${truckColor.bodyDark}" stroke-width="1" opacity="0.5"/>
      <line x1="240" y1="160" x2="240" y2="360" stroke="${truckColor.bodyDark}" stroke-width="1" opacity="0.5"/>
      <line x1="260" y1="160" x2="260" y2="360" stroke="${truckColor.bodyDark}" stroke-width="1" opacity="0.5"/>
      
      <!-- Windshield -->
      <rect x="90" y="120" width="70" height="100" rx="12" fill="#bae6fd" opacity="0.7"/>
      
      <!-- Hood detail -->
      <rect x="120" y="100" width="20" height="140" rx="10" fill="#ffffff" opacity="0.25"/>
      
      <!-- Wheels (larger for truck) -->
      <rect x="40" y="200" width="35" height="100" rx="16" fill="#111827"/>
      <rect x="40" y="320" width="35" height="100" rx="16" fill="#111827"/>
      <rect x="225" y="200" width="35" height="100" rx="16" fill="#111827"/>
      <rect x="225" y="320" width="35" height="100" rx="16" fill="#111827"/>
      
      <!-- Wheel rims -->
      <rect x="48" y="220" width="19" height="60" rx="9" fill="#9ca3af"/>
      <rect x="48" y="340" width="19" height="60" rx="9" fill="#9ca3af"/>
      <rect x="233" y="220" width="19" height="60" rx="9" fill="#9ca3af"/>
      <rect x="233" y="340" width="19" height="60" rx="9" fill="#9ca3af"/>
      
      <!-- Headlights -->
      <rect x="90" y="80" width="35" height="15" rx="7" fill="#fde047"/>
      <rect x="125" y="80" width="35" height="15" rx="7" fill="#fde047"/>
      
      <!-- Tail lights -->
      <rect x="270" y="340" width="10" height="20" rx="5" fill="#7f1d1d"/>
      
      <!-- Center direction line -->
      <line x1="150" y1="50" x2="150" y2="550" stroke="#ffffff" stroke-width="3" stroke-dasharray="10,10" opacity="0.5"/>
    </svg>`;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(TruckIcon)}`;
  };

  // Theme management
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  // Animated vehicle movement on road
  useEffect(() => {
    const interval = setInterval(() => {
      setVehicles((prevVehicles) =>
        prevVehicles.map((vehicle) => {
          let newPosition;
          if (vehicle.direction === 'left') {
            newPosition = vehicle.position >= 100 ? 0 : vehicle.position + vehicle.speed;
          } else {
            newPosition = vehicle.position <= 0 ? 100 : vehicle.position - vehicle.speed;
          }
          return {
            ...vehicle,
            position: newPosition,
          };
        })
      );
    }, 30); // Reduced interval for smoother animation
    return () => clearInterval(interval);
  }, []);

  const stats = [
    { icon: Users, value: '50K+', label: 'Active Users', color: 'from-blue-500 to-cyan-500' },
    { icon: Car, value: '100K+', label: 'Vehicles Tracked', color: 'from-purple-500 to-pink-500' },
    { icon: Globe, value: '150+', label: 'Countries', color: 'from-green-500 to-emerald-500' },
    { icon: Award, value: '99.9%', label: 'Uptime', color: 'from-orange-500 to-red-500' },
  ];

  const milestones = [
    {
      year: '2024',
      title: 'Founded',
      description: 'TrackYourWheelz was born with a vision to revolutionize fleet management.',
      icon: Target
    },
    {
      year: '2025',
      title: 'First 2K Users',
      description: 'Reached our first major milestone with 10,000 active fleet managers.',
      icon: TrendingUp
    }
  ];

  const team = [
    {
      name: 'Innovation',
      description: 'Cutting-edge technology and continuous improvement',
      icon: Zap,
      color: 'from-yellow-500 to-orange-500'
    },
    {
      name: 'Reliability',
      description: '99.9% uptime guarantee and 24/7 support',
      icon: Shield,
      color: 'from-green-500 to-emerald-500'
    },
    {
      name: 'Excellence',
      description: 'Award-winning platform trusted by industry leaders',
      icon: Award,
      color: 'from-blue-500 to-purple-500'
    },
  ];

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes roadMove {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 100px 0;
          }
        }
        @keyframes trackingPulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.2);
          }
        }
        @keyframes devicePulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
          }
          50% {
            transform: scale(1.1);
            box-shadow: 0 0 0 10px rgba(59, 130, 246, 0);
          }
        }
        @keyframes signalWave {
          0% {
            opacity: 1;
            transform: scale(0.8);
          }
          100% {
            opacity: 0;
            transform: scale(2);
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.8s ease-out;
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .road-pattern {
          background-image: repeating-linear-gradient(
            90deg,
            transparent,
            transparent 40px,
            ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} 40px,
            ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} 80px
          );
          animation: roadMove 0.8s linear infinite;
        }
        .device-pulse {
          animation: devicePulse 2s ease-in-out infinite;
        }
        .signal-wave {
          animation: signalWave 2s ease-out infinite;
        }
        .tracking-indicator {
          animation: trackingPulse 1.5s ease-in-out infinite;
        }
        .glass {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .glass-dark {
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        .gradient-text {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      <div className={`min-h-screen relative overflow-hidden transition-all duration-500 ${
        theme === 'dark' 
          ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e]' 
          : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
      }`}>
      <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="About" />
        
        {/* Background Elements */}
        <div className="absolute inset-0">
          {theme === 'dark' ? (
            // Dark theme background - Purple to Dark gradient (left to right)
            <>
              <div className="absolute inset-0 bg-gradient-to-r from-[#483D8B] to-[#2d1b4e]"></div>
            </>
          ) : (
            // Light theme background
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"></div>
              <div className="absolute top-20 left-20 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-200/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-indigo-200/30 rounded-full blur-3xl animate-pulse delay-500"></div>
            </>
          )}
        </div>

        {/* Hero Section with Road Animation */}
        <section className="relative z-10 pt-24 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <div className="inline-block mb-6">
                <span className={`px-4 py-2 rounded-full border text-sm font-medium transition-all duration-500 ${
                  theme === 'dark'
                    ? 'bg-purple-500/20 border-purple-500/30 text-purple-300'
                    : 'bg-purple-100 border-purple-200 text-purple-700'
                }`}>
                  About TrackYourWheelz
                </span>
              </div>
              
              <h1 className={`text-sm md:text-2xl lg:text-3xl font-black leading-tight mb-6 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                <span className="block md:inline">Your Journey to</span>
                <span className="block gradient-text text-sm md:text-4xl">Smart Fleet Management</span>
              </h1>
              
              <p className={`max-w-2xl mx-auto text-sm md:text-base leading-relaxed ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                We're transforming the way businesses track, manage, and optimize their vehicle fleets with cutting-edge GPS technology and intelligent analytics.
              </p>
            </div>

            {/* Road Card with Moving Device */}
            <Card className={`${theme === 'dark' ? 'glass-dark' : 'glass'} border-0 shadow-2xl overflow-hidden relative`}>
              <CardContent className="p-0 space-y-6 md:space-y-0">
                {/* Road Visualization */}
                <div className={`relative h-64 md:h-80 overflow-hidden ${
                  theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-200/50'
                }`}>
                  {/* Road Surface */}
                  <div className={`absolute inset-0 road-pattern ${
                    theme === 'dark' ? 'bg-gray-900/80' : 'bg-gray-300/80'
                  }`}></div>
                  
                  {/* Road Center Line */}
                  <div className="absolute top-1/2 left-0 right-0 h-1 transform -translate-y-1/2">
                    <div className={`h-full w-full ${
                      theme === 'dark' 
                        ? 'bg-gradient-to-r from-transparent via-yellow-400/60 to-transparent' 
                        : 'bg-gradient-to-r from-transparent via-yellow-500/80 to-transparent'
                    }`} style={{
                      backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 20px, currentColor 20px, currentColor 40px)'
                    }}></div>
                  </div>

                  {/* Road Edges */}
                  <div className={`absolute top-0 left-0 right-0 h-2 ${
                    theme === 'dark' ? 'bg-white/20' : 'bg-white/60'
                  }`}></div>
                  <div className={`absolute bottom-0 left-0 right-0 h-2 ${
                    theme === 'dark' ? 'bg-white/20' : 'bg-white/60'
                  }`}></div>

                  {/* Moving Tracking Vehicles */}
                  {vehicles.map((vehicle) => {
                    const colorMap = {
                      red: 'rgba(220, 38, 38, 0.3)',
                      blue: 'rgba(37, 99, 235, 0.3)',
                      green: 'rgba(22, 163, 74, 0.3)',
                      purple: 'rgba(147, 51, 234, 0.3)',
                      orange: 'rgba(234, 88, 12, 0.3)',
                    };
                    const waveColor = colorMap[vehicle.color] || colorMap.red;
                    const rotation = vehicle.direction === 'left' ? '-90deg' : '90deg';
                    const iconSrc = vehicle.type === 'truck' ? getTruckIcon(vehicle.color) : getCarIcon(vehicle.color);
                    const iconSize = vehicle.type === 'truck' ? 'w-12 h-24' : 'w-10 h-20';
                    
                    return (
                      <div
                        key={vehicle.id}
                        className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-1/2 device-pulse"
                        style={{
                          left: `${vehicle.position}%`,
                          transition: 'left 0.03s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        {/* Vehicle Icon */}
                        <div className="relative flex items-center justify-center shadow-2xl">
                          <img
                            src={iconSrc}
                            alt={vehicle.name}
                            className={`${iconSize} object-contain drop-shadow-2xl transition-transform duration-200`}
                            style={{
                              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
                              transform: `rotate(${rotation})`,
                            }}
                          />
                          
                          {/* Tracking Indicator Badge */}
                          <div
                            className="absolute -top-2 -right-2 w-4 h-4 rounded-full border-2 border-white shadow-lg tracking-indicator"
                            style={{
                              backgroundColor: vehicle.color === 'red' ? '#dc2626' :
                                             vehicle.color === 'blue' ? '#2563eb' :
                                             vehicle.color === 'green' ? '#16a34a' :
                                             vehicle.color === 'purple' ? '#9333ea' :
                                             '#ea580c',
                            }}
                            title="Tracking Active"
                          >
                            <div
                              className="absolute inset-0 rounded-full"
                              style={{
                                backgroundColor: vehicle.color === 'red' ? '#dc2626' :
                                               vehicle.color === 'blue' ? '#2563eb' :
                                               vehicle.color === 'green' ? '#16a34a' :
                                               vehicle.color === 'purple' ? '#9333ea' :
                                               '#ea580c',
                                opacity: 0.6,
                                animation: 'trackingPulse 1.5s ease-in-out infinite',
                              }}
                            ></div>
                          </div>
                          
                          {/* Signal Waves */}
                          <div
                            className="absolute inset-0 rounded-xl signal-wave"
                            style={{
                              width: '80px',
                              height: '80px',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              backgroundColor: waveColor,
                            }}
                          ></div>
                          <div
                            className="absolute inset-0 rounded-xl signal-wave"
                            style={{
                              width: '80px',
                              height: '80px',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              backgroundColor: waveColor.replace('0.3', '0.2'),
                              animationDelay: '0.3s',
                            }}
                          ></div>
                          <div
                            className="absolute inset-0 rounded-xl signal-wave"
                            style={{
                              width: '80px',
                              height: '80px',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              backgroundColor: waveColor.replace('0.3', '0.1'),
                              animationDelay: '0.6s',
                            }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Road Markers */}
                  <div className="absolute top-1/2 left-10 transform -translate-y-1/2 hidden md:block">
                    <MapPin className={`w-6 h-6 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'} animate-float`} style={{ animationDelay: '0s' }} />
                  </div>
                  <div className="absolute top-1/2 right-10 transform -translate-y-1/2 hidden md:block">
                    <MapPin className={`w-6 h-6 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} animate-float`} style={{ animationDelay: '1s' }} />
                  </div>
                </div>

                {/* Info Section Below Road */}
                <div className={`p-6 md:p-8 ${
                  theme === 'dark' ? 'bg-gray-900/30' : 'bg-white/30'
                }`}>
                  <div className="grid md:grid-cols-3 gap-6 text-sm md:text-base">
                    <div className="text-center">
                      <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center`}>
                        <Activity className="w-6 h-6 text-white" />
                      </div>
                      <h3 className={`text-sm md:text-lg font-bold mb-2 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        Real-Time Tracking
                      </h3>
                      <p className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Live location updates every second
                      </p>
                    </div>
                    <div className="text-center">
                      <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center`}>
                        <Route className="w-6 h-6 text-white" />
                      </div>
                      <h3 className={`text-sm md:text-lg font-bold mb-2 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        Route Optimization
                      </h3>
                      <p className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        AI-powered route planning
                      </p>
                    </div>
                    <div className="text-center">
                      <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center`}>
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <h3 className={`text-sm md:text-lg font-bold mb-2 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        Secure & Reliable
                      </h3>
                      <p className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        99.9% uptime guarantee
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Stats Section */}
        <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-sm md:text-base">
              {stats.map((stat, index) => (
                <Card
                  key={index}
                  className={`${theme === 'dark' ? 'glass-dark' : 'glass'} border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105`}
                >
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className={`text-sm md:text-3xl font-black mb-2 gradient-text`}>
                      {stat.value}
                    </div>
                    <div className={`text-xs md:text-base font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {stat.label}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Vision Section */}
        <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className={`text-sm md:text-2xl lg:text-3xl font-black mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Our Story
              </h2>
              <p className={`text-sm md:text-base ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Driven by innovation, powered by technology
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-12">
              <Card className={`${theme === 'dark' ? 'glass-dark' : 'glass'} border-0 shadow-xl hover:shadow-2xl transition-all duration-500`}>
                <CardHeader>
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mb-4`}>
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className={`text-sm md:text-2xl mb-3 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Our Mission
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={`text-sm md:text-base leading-relaxed ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    To empower businesses worldwide with intelligent fleet management solutions that drive efficiency, reduce costs, and enhance safety. We believe every vehicle deserves smart tracking, and every fleet manager deserves peace of mind.
                  </p>
                </CardContent>
              </Card>

              <Card className={`${theme === 'dark' ? 'glass-dark' : 'glass'} border-0 shadow-xl hover:shadow-2xl transition-all duration-500`}>
                <CardHeader>
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4`}>
                    <Navigation className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className={`text-sm md:text-2xl mb-3 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Our Vision
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={`text-sm md:text-base leading-relaxed ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    To become the global leader in fleet management technology, setting new standards for innovation, reliability, and customer satisfaction. We envision a future where every fleet operates at peak efficiency through intelligent automation.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Journey Timeline */}
        <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className={`text-sm md:text-2xl lg:text-2xl font-black mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Our Journey
              </h2>
              <p className={`text-sm md:text-xl ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Milestones that shaped our growth
              </p>
            </div>

            <div className="relative">
              {/* Timeline Line */}
              <div className={`hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 ${
                theme === 'dark' ? 'bg-gradient-to-b from-purple-500/50 to-blue-500/50' : 'bg-gradient-to-b from-purple-300/50 to-blue-300/50'
              }`} style={{ top: '2rem', bottom: '2rem' }}></div>

              <div className="space-y-12">
                {milestones.map((milestone, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col gap-4 md:gap-0 md:flex-row md:items-center ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Timeline Dot */}
                    <div className={`hidden md:block absolute left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full bg-gradient-to-br ${stats[index % stats.length].color} border-4 ${
                      theme === 'dark' ? 'border-gray-800' : 'border-white'
                    } shadow-lg z-10`}></div>

                    {/* Content Card */}
                    <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8 md:ml-auto'}`}>
                      <Card className={`${theme === 'dark' ? 'glass-dark' : 'glass'} border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105`}>
                        <CardContent className="p-6">
                          <div className="flex items-start space-x-4">
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stats[index % stats.length].color} flex items-center justify-center flex-shrink-0`}>
                              <milestone.icon className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className={`text-xs md:text-sm font-semibold mb-1 ${
                                theme === 'dark' ? 'text-purple-300' : 'text-purple-600'
                              }`}>
                                {milestone.year}
                              </div>
                              <h3 className={`text-sm md:text-xl font-bold mb-2 ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>
                                {milestone.title}
                              </h3>
                              <p className={`text-sm md:text-base leading-relaxed ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {milestone.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Values Section */}
        <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className={`text-sm md:text-2xl lg:text-2xl font-black mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                What Sets Us Apart
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {team.map((item, index) => (
                <Card
                  key={index}
                  className={`${theme === 'dark' ? 'glass-dark' : 'glass'} border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 group cursor-pointer`}
                >
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <item.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className={`text-sm md:text-xl font-bold mb-3 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {item.name}
                    </h3>
                    <p className={`text-sm md:text-base leading-relaxed ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className={`${theme === 'dark' ? 'glass-dark' : 'glass'} border-0 shadow-2xl text-center overflow-hidden relative`}>
              <CardContent className="p-12 md:p-16">
                <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center animate-float`}>
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <h2 className={`text-sm md:text-2xl font-black mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Ready to Transform Your Fleet?
                </h2>
                <p className={`text-sm md:text-base mb-8 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Join thousands of businesses already using TrackYourWheelz to optimize their operations.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => navigate('/login')}
                    className={`h-12 px-8 text-lg font-semibold transition-all duration-300 hover:scale-105 ${
                      theme === 'dark'
                        ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e] text-white'
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                    }`}
                  >
                    Get Started
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/home')}
                    className={`h-12 px-8 text-lg font-semibold transition-all duration-300 hover:scale-105 ${
                      theme === 'dark'
                        ? 'border-white/20 hover:bg-white/10 text-white'
                        : 'border-gray-300 hover:bg-gray-100 text-gray-900'
                    }`}
                  >
                    Learn More
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;
